import { useEffect, useRef, useState } from 'react';
import { Modal } from '@components/ui';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { templateService } from '../services/templateService';
import Mustache from 'mustache';

interface EmailTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  sendGridTemplateId?: string; // SendGrid template ID from emailTemplate.emailTemplateId
  colors: {
    background: string;
    primary: string;
    text: string;
  };
  advancedColors?: {
    backgroundBodyColor?: string;
    backgroundBodyDecorationColor?: string;
    backgroundFooterColor?: string;
    backgroundHeaderColor?: string;
    buttonRedirectEivoiceColor?: string;
    buttonRedirectWebSiteColor?: string;
    textButtonRedirectEivoiceColor?: string;
    textButtonRedirectWebSiteColor?: string;
    textPrimaryColor?: string;
    textSegundaryColor?: string;
  };
  logo: string | null;
}

export function EmailTemplateModal({ isOpen, onClose, templateId, sendGridTemplateId, colors, advancedColors, logo }: EmailTemplateModalProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renderedHtml, setRenderedHtml] = useState<string>('');

  useEffect(() => {
    if (isOpen && templateId) {
      loadTemplate();
    }
  }, [isOpen, templateId]);

  // Separate effect to write to iframe once HTML is ready and iframe is mounted
  useEffect(() => {
    if (renderedHtml && iframeRef.current) {
      const iframeDoc = iframeRef.current.contentDocument;
      if (iframeDoc) {
        console.log('Writing to iframe...');
        iframeDoc.open();
        iframeDoc.write(renderedHtml);
        iframeDoc.close();
        console.log('Iframe updated successfully');
      }
    }
  }, [renderedHtml]);

  // Helper function to get the first valid (non-empty) color value
  const getValidColor = (...values: (string | undefined)[]): string => {
    for (const value of values) {
      if (value && value.trim() !== '') {
        return value;
      }
    }
    return values[values.length - 1] || '#000000'; // Return last value or black as ultimate fallback
  };

  const convertJinja2ToMustache = (template: string, data: any, effectiveColors?: { background: string; primary: string; text: string }): string => {
    let result = template;

    // Use effectiveColors if provided, otherwise fall back to props colors
    const colorsToUse = effectiveColors || colors;

    // Replace CSS variables in :root with actual values from the template configuration
    // Priority: template data > advanced colors > basic colors > defaults
    const cssVarMap: Record<string, string> = {
      'BackgroundHeaderColor': getValidColor(data.BackgroundHeaderColor, colorsToUse.primary, '#54A205'),
      'BackgroundBodyColor': getValidColor(data.BackgroundBodyColor, colorsToUse.background, '#D4D4D4'),
      'BackgroundBodyDecorationColor': getValidColor(data.BackgroundBodyDecorationColor, colorsToUse.primary, '#FFFFFF'),
      'BackgroundFooterColor': getValidColor(data.BackgroundFooterColor, colorsToUse.background, '#D4D4D4'),
      'TextPrimaryColor': getValidColor(data.TextPrimaryColor, colorsToUse.text, '#000000'),
      'TextSegundaryColor': getValidColor(data.TextSegundaryColor, colorsToUse.text, '#000000'),
      'ButtonRedirectWebSiteColor': getValidColor(data.ButtonRedirectWebSiteColor, colorsToUse.background, '#FFFFFF'),
      'TextButtonRedirectWebSiteColor': getValidColor(data.TextButtonRedirectWebSiteColor, colorsToUse.primary, '#000000'),
      'ButtonRedirectEivoiceColor': getValidColor(data.ButtonRedirectEivoiceColor, colorsToUse.primary, '#101010'),
      'TextButtonRedirectEivoiceColor': getValidColor(data.TextButtonRedirectEivoiceColor, colorsToUse.background, '#F9F9F9'),
    };

    // Replace var(--VariableName) with actual values
    Object.keys(cssVarMap).forEach((varName) => {
      const regex = new RegExp(`var\\(--${varName}\\s*\\)`, 'g');
      result = result.replace(regex, cssVarMap[varName]);
    });

    // Convert Jinja2 for loops to Mustache
    // {% for Item in FElectronica.gItem %} → {{#FElectronica.gItem}}
    result = result.replace(/\{% for (\w+) in ([^\s%]+) %\}/g, '{{#$2}}');

    // {% endfor %} → {{/arrayName}}
    // We need to track the array names to close them properly
    const forMatches = [...template.matchAll(/\{% for \w+ in ([^\s%]+) %\}/g)];
    forMatches.reverse().forEach((match) => {
      const arrayName = match[1];
      result = result.replace(/\{% endfor %\}/, `{{/${arrayName}}}`);
    });

    // Handle conditionals: Remove else blocks first, then remove if/endif tags
    // Remove content between {% else %} and {% endif %} (we don't need else branches)
    result = result.replace(/\{% else %\}[\s\S]*?\{% endif %\}/g, '{% endif %}');

    // Now remove {% if %} and {% endif %} tags but KEEP the content between them
    result = result.replace(/\{% if [^%]+ %\}/g, '');
    result = result.replace(/\{% endif %\}/g, '');
    result = result.replace(/\{% else %\}/g, '');

    // Replace {{#if}} handlebars conditionals - keep content
    result = result.replace(/\{\{#if ([^}]+)\}\}/g, '');
    result = result.replace(/\{\{\/if\}\}/g, '');

    return result;
  };

  const loadTemplate = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Use the SendGrid template ID passed from the parent or fall back to hardcoded one
      const templateIdToUse = sendGridTemplateId || '63140077e565410032fac0d6';
      console.log('Fetching SendGrid template with ID:', templateIdToUse);

      const template = await templateService.getSendGridTemplate(templateIdToUse);
      console.log('Template received:', {
        hasHtml: !!template.templateHtml,
        hasJson: !!template.jsonTestModel,
        htmlLength: template.templateHtml?.length,
        jsonLength: template.jsonTestModel?.length,
      });

      // Parse the JSON test model (it's a string, so we need to parse it)
      const testData = JSON.parse(template.jsonTestModel);
      console.log('Test data parsed:', Object.keys(testData));

      // Use colors from configuration, or fall back to colors from jsonTestModel
      const effectiveColors = {
        background: colors.background || testData.ColorFooter || '#dbe5ea',
        primary: colors.primary || testData.ColorPrimary || '#242b3d',
        text: colors.text || testData.ColorHeader || '#126de5',
      };

      console.log('Using colors from template configuration:', {
        background: colors.background,
        primary: colors.primary,
        text: colors.text,
      });
      console.log('Effective colors (with fallbacks):', effectiveColors);
      console.log('Using logo:', logo);

      // Add logo to test data if available
      const templateData = {
        ...testData,
        Logo: logo || '',
        // Also add the effective colors to template data in case they're used as variables
        ColorPrimary: effectiveColors.primary,
        ColorHeader: effectiveColors.text,
        ColorFooter: effectiveColors.background,
        // Add advanced colors if provided
        ...(advancedColors && {
          BackgroundHeaderColor: advancedColors.backgroundHeaderColor,
          BackgroundBodyColor: advancedColors.backgroundBodyColor,
          BackgroundBodyDecorationColor: advancedColors.backgroundBodyDecorationColor,
          BackgroundFooterColor: advancedColors.backgroundFooterColor,
          TextPrimaryColor: advancedColors.textPrimaryColor,
          TextSegundaryColor: advancedColors.textSegundaryColor,
          ButtonRedirectWebSiteColor: advancedColors.buttonRedirectWebSiteColor,
          TextButtonRedirectWebSiteColor: advancedColors.textButtonRedirectWebSiteColor,
          ButtonRedirectEivoiceColor: advancedColors.buttonRedirectEivoiceColor,
          TextButtonRedirectEivoiceColor: advancedColors.textButtonRedirectEivoiceColor,
        }),
      };

      // Convert Jinja2 template to Mustache syntax and inject CSS variables
      // Pass effective colors instead of the potentially null colors
      const mustacheTemplate = convertJinja2ToMustache(template.templateHtml, templateData, effectiveColors);
      console.log('Template converted, length:', mustacheTemplate.length);

      // Render the template with Mustache
      const html = Mustache.render(mustacheTemplate, templateData);
      console.log('Template rendered, length:', html.length);

      // Save rendered HTML to state - the useEffect will write it to iframe
      setRenderedHtml(html);
    } catch (err) {
      console.error('Error loading template:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar el template');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      kicker="Plantillas · Email"
      title="Vista Previa del Template de Email"
      icon={<EnvelopeIcon />}
      size="xl"
      data-testid="email-template-modal"
    >
      <div className="space-y-4">
        {isLoading && (
          <div className="flex justify-center py-12" data-testid="email-template-modal-loading-spinner">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        )}

        {error && (
          <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4" data-testid="email-template-modal-error-alert">
            <p className="text-danger-800 dark:text-danger-200 text-sm">{error}</p>
          </div>
        )}

        {!isLoading && !error && (
          <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden">
            <iframe
              ref={iframeRef}
              title="Email Template Preview"
              className="w-full h-[600px] bg-white"
              sandbox="allow-same-origin"
              data-testid="email-template-modal-preview-iframe"
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
